import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import APITokenConfiguration from 'components/settings/externalIntegrationConfiguration/components/APITokenConfiguration';
import ProcoreIntegrationConfiguration from 'components/settings/externalIntegrationConfiguration/components/ProcoreIntegrationConfiguration';
import SimproIntegrationConfiguration from 'components/settings/externalIntegrationConfiguration/components/SimproIntegrationConfiguration';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function ExternalIntegrationConfiguration({
  hasAPIIntegrationAccess,
  hasProcoreIntegrationAccess,
  hasSimproIntegrationAccess,
  integratingSimproCompanyIntegrationId,
  isMultiCompany,
  isProcoreSession,
  missingProcoreAccountRequirement,
  procoreAccountIntegration,
  procoreProjectIntegrationCount,
  seedAPIToken,
  seedProcoreIdentity,
  seedSimproCompanyIntegrations,
  seedSimproProviderConfigurations,
}) {
  const { t: i18nTSettings } = useTranslation('common', {
    keyPrefix: 'settings',
  });

  const isAPITokenSectionVisible = hasAPIIntegrationAccess;

  const isIntegrationsSectionVisible =
    hasProcoreIntegrationAccess ||
    isProcoreSession ||
    hasSimproIntegrationAccess;

  return (
    <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-10'>
      {isAPITokenSectionVisible && (
        <>
          <h3 className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
            {i18nTSettings('apiToken')}
          </h3>
          <APITokenConfiguration seedAPIToken={seedAPIToken} />
        </>
      )}
      {isAPITokenSectionVisible && isIntegrationsSectionVisible && (
        <hr className='tw-mb-10 tw-mt-10' />
      )}
      {isIntegrationsSectionVisible && (
        <>
          <h3 className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
            {i18nTSettings('integrations')}
          </h3>
          <div className='tw-flex tw-flex-col tw-gap-6'>
            {(hasProcoreIntegrationAccess || isProcoreSession) && (
              <ProcoreIntegrationConfiguration
                isProcoreSession={isProcoreSession}
                missingProcoreAccountRequirement={
                  missingProcoreAccountRequirement
                }
                procoreAccountIntegration={procoreAccountIntegration}
                procoreProjectIntegrationCount={procoreProjectIntegrationCount}
                seedProcoreIdentity={seedProcoreIdentity}
              />
            )}
            {hasSimproIntegrationAccess && (
              <SimproIntegrationConfiguration
                integratingSimproCompanyIntegrationId={
                  integratingSimproCompanyIntegrationId
                }
                isMultiCompany={isMultiCompany}
                seedSimproCompanyIntegrations={seedSimproCompanyIntegrations}
                seedSimproProviderConfigurations={
                  seedSimproProviderConfigurations
                }
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

ExternalIntegrationConfiguration.propTypes = {
  hasAPIIntegrationAccess: PropTypes.bool.isRequired,
  hasProcoreIntegrationAccess: PropTypes.bool.isRequired,
  hasSimproIntegrationAccess: PropTypes.bool.isRequired,
  isMultiCompany: PropTypes.bool.isRequired,
  missingProcoreAccountRequirement: PropTypes.string,
  procoreProjectIntegrationCount: PropTypes.number.isRequired,
  procoreAccountIntegration: PropTypes.object,
  seedProcoreIdentity: PropTypes.object,
  isProcoreSession: PropTypes.bool,
  seedAPIToken: PropTypes.shape({
    data: resourceShape('apiToken'),
  }),
  seedSimproCompanyIntegrations: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('simproCompanyIntegration'))
      .isRequired,
    included: PropTypes.array,
  }).isRequired,
  seedSimproProviderConfigurations: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('simproProviderConfiguration'))
      .isRequired,
  }).isRequired,
  integratingSimproCompanyIntegrationId: PropTypes.string,
};
